import dayjs from "dayjs";
import * as chrono from "chrono-node";

/**
 * Extract dollar amount from text: "cost $100", "$100", "100 dollars".
 * @returns {{ value: number, matched: string }|null}
 */
function parseAmount(phrase) {
  if (!phrase || typeof phrase !== "string") return null;
  const trimmed = phrase.trim();
  const m = trimmed.match(/(?:cost\s+)?\$?(\d+(?:\.\d{1,2})?)\s*(?:dollars?)?/i);
  if (!m) return null;
  const value = parseFloat(m[1], 10);
  return Number.isFinite(value) ? { value, matched: m[0] } : null;
}

/**
 * Extract time and date from natural text using chrono-node (open-source NLP date parser).
 * Also extracts amount and repeat via simple regex.
 *
 * @param {string} text - Natural language input (e.g. "Income tax return due by April 15, 2026 at 3pm, $50, weekly")
 * @returns {{ name: string, due_date: string, due_time: string, amount: number, repeat: number }}
 */
export function parseNaturalTaskText(text) {
  const result = {
    name: "",
    due_date: dayjs().format("YYYY-MM-DD"),
    due_time: "",
    amount: 0,
    repeat_rule: null,
  };
  if (!text || typeof text !== "string") return result;
  let remaining = text.trim();
  if (!remaining) return result;

  const refDate = new Date();
  const options = { forwardDate: true };

  // 1. Parse date/time with chrono-node (handles "April 15, 2026", "due by April 15th", "tomorrow at 4pm", etc.)
  let parsedDate = null;
  let parsedTime = "";
  let dateMatchIndex = -1;
  let dateMatchLength = 0;

  try {
    const results = chrono.parse(remaining, refDate, options);
    if (results && results.length > 0) {
      const first = results[0];
      const startDate = first.start.date();
      parsedDate = dayjs(startDate);
      if (parsedDate.isValid()) {
        dateMatchIndex = first.index;
        dateMatchLength = first.text.length;
        if (first.start.isCertain("hour") || first.start.isCertain("minute")) {
          const hour = first.start.get("hour") ?? 0;
          const minute = first.start.get("minute") ?? 0;
          parsedTime = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
        }
      }
    }
  } catch (_) {
    // If chrono fails, keep default (today)
  }

  // Remove the date phrase from remaining to get the task name (chrono gives exact span)
  if (dateMatchIndex >= 0 && dateMatchLength > 0) {
    remaining =
      remaining.slice(0, dateMatchIndex) + " " + remaining.slice(dateMatchIndex + dateMatchLength);
  }

  // 2. Amount: "cost $100", "$100", "100 dollars"
  const amountPatterns = [
    /\bcost\s+\$?\d+(?:\.\d{1,2})?\s*(?:dollars?)?\b/gi,
    /\$\d+(?:\.\d{1,2})?\s*(?:dollars?)?\b/gi,
    /\b\d+(?:\.\d{1,2})?\s*dollars?\b/gi,
  ];
  for (const re of amountPatterns) {
    const m = remaining.match(re);
    if (m) {
      const amountResult = parseAmount(m[0]);
      if (amountResult && amountResult.value >= 0) {
        result.amount = amountResult.value;
        remaining = remaining.replace(m[0], " ").replace(/\s+/g, " ").trim();
        break;
      }
    }
  }

  // 3. Repeat: "monthly", "every 2 weeks", "quarterly", etc.
  const repeatPatterns = [
    { re: /\b(yearly|annually|every\s+year)\b/gi, rule: { frequency: 'yearly', interval: 1 } },
    { re: /\b(quarterly|every\s+quarter)\b/gi, rule: { frequency: 'monthly', interval: 3 } },
    { re: /\b(semiannual|every\s+6\s+months|twice\s+a\s+year)\b/gi, rule: { frequency: 'monthly', interval: 6 } },
    { re: /\b(monthly|every\s+month|repeats?\s+monthly|monthly\s+recurring)\b/gi, rule: { frequency: 'monthly', interval: 1 } },
    { re: /\b(bi-?weekly|every\s+two\s+weeks|fortnightly)\b/gi, rule: { frequency: 'weekly', interval: 2 } },
    { re: /\b(weekly|every\s+week|repeats?\s+weekly|weekly\s+recurring)\b/gi, rule: { frequency: 'weekly', interval: 1 } },
    { re: /\b(daily|every\s+day|repeats?\s+daily|daily\s+recurring)\b/gi, rule: { frequency: 'daily', interval: 1 } },
  ];
  for (let i = 0; i < repeatPatterns.length; i++) {
    const m = remaining.match(repeatPatterns[i].re);
    if (m) {
      result.repeat_rule = { ...repeatPatterns[i].rule };
      remaining = remaining.replace(m[0], " ").replace(/\s+/g, " ").trim();
      break;
    }
  }

  if (parsedDate && parsedDate.isValid()) {
    result.due_date = parsedDate.format("YYYY-MM-DD");
  }
  if (parsedTime) {
    result.due_time = parsedTime;
  }

  // Task name: remaining text, cleaned (commas, extra spaces, trailing " is due by" etc.)
  let name = remaining.replace(/\s+/g, " ").replace(/,+\s*$/, "").replace(/^,+\s*/, "").trim() || text.trim();
  name = name.replace(/\s+(?:is\s*)?(?:due\s*)?(?:by\s*)?$/i, "").trim();
  result.name = name || text.trim();

  return result;
}
